import React, { useMemo, useState, useEffect } from "react";
import { App as AntdApp, Card, Space, Table, Tag, Tooltip, Typography } from "antd";
import { BranchesOutlined, DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import FlyerActionsMenu from "./FlyerActionsMenu.jsx";
import { triggerBrowserDownload } from "../utils/download.js";
import { formatPdfPageSize } from "../utils/pdfPageSize.js";
import "../pages/VolantiniPage.css";

const { Text } = Typography;
const fmtSO = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—");

const STATUS_STYLES = {
	NEW: { text: "Bozza", tagColor: "gold" },
	READY: { text: "Pronto", tagColor: "geekblue" },
	PROCESSING: { text: "In lavorazione", tagColor: "blue" },
	DONE: { text: "Completato", tagColor: "green" },
	ERROR: { text: "Errore", tagColor: "red" },
};

/**
 * Tabella volantini riusabile.
 *
 * Props:
 * - flyers: array di volantini (già caricati dal chiamante)
 * - loading: boolean
 * - isTrash: boolean - se true mostra colonna "Eliminato" e disabilita il click di riga
 * - flat: boolean - se true tratta varianti come righe normali (no expand)
 *                   se false (default) padri espandibili con varianti sotto
 * - onManage: callback(flyer) chiamata al click di riga (default: navigate /flyermanage/:id)
 * - onRefresh: callback chiamata dopo azioni del menu (default: noop)
 * - pageSize: default 10
 * - embedded: se true non avvolge la tabella nel Card esterno (per uso dentro
 *             una sezione/raggruppamento che porta già il proprio contenitore)
 */
export default function FlyersTable({
	flyers = [],
	loading = false,
	isTrash = false,
	flat = false,
	onManage,
	onRefresh,
	pageSize = 10,
	embedded = false
}) {
	const navigate = useNavigate();
	const { message } = AntdApp.useApp();

	// Quando NON è flat: separa padri e varianti, varianti vanno in expand sotto i padri
	const { parentRows, variantsByParentId } = useMemo(() => {
		if (flat || isTrash)
			return { parentRows: flyers, variantsByParentId: new Map() };

		const parents = flyers.filter(f => !f.isVariant);
		const variants = flyers.filter(f => f.isVariant);
		const byParent = new Map();
		variants.forEach(v => {
			const pid = v.parentFlyerId;
			if (!pid) return;
			if (!byParent.has(pid)) byParent.set(pid, []);
			byParent.get(pid).push(v);
		});
		const parentIds = new Set(parents.map(p => p._id || p.id));
		const orphaned = variants.filter(v => !parentIds.has(v.parentFlyerId));

		const maxCreatedAt = (row) => {
			const children = byParent.get(row._id || row.id) || [];
			return Math.max(
				new Date(row.createdAt || 0).getTime(),
				...children.map(v => new Date(v.createdAt || 0).getTime())
			);
		};
		const sorted = [...parents, ...orphaned].sort((a, b) => maxCreatedAt(b) - maxCreatedAt(a));
		return { parentRows: sorted, variantsByParentId: byParent };
	}, [flyers, flat, isTrash]);

	const [expandedRowKeys, setExpandedRowKeys] = useState([]);
	const [hasInitialData, setHasInitialData] = useState(false);
	useEffect(() => {
		if (!flat && !isTrash && !hasInitialData && variantsByParentId.size > 0) {
			setExpandedRowKeys([...variantsByParentId.keys()]);
			setHasInitialData(true);
		}
	}, [variantsByParentId, isTrash, flat, hasInitialData]);

	const handleRowClick = (record) => {
		if (isTrash) return;
		if (typeof onManage === "function") onManage(record);
		else navigate(`/flyermanage/${record._id || record.id}`);
	};

	const handleDownloadPdf = async (event, flyer) => {
		event?.stopPropagation?.();
		const flyerId = flyer?._id || flyer?.id;
		if (!flyerId) {
			message.error("ID volantino non disponibile");
			return;
		}
		const hideLoading = message.loading("Download PDF in corso...", 0);
		try {
			triggerBrowserDownload(`/api/flyers/${flyerId}/export.pdf`);
		}
		catch (error) {
			message.error(error?.message || "Errore download PDF");
		}
		finally {
			if (typeof hideLoading === "function") hideLoading();
		}
	};

	const columns = [
		{
			title: "Volantino",
			dataIndex: "name",
			render: (_, r) => (
				<Space size={12}>
					<div className={`vp-cover${r.isVariant ? " vp-cover--variant" : ""}`} />
					<div className="vp-title">
						<Space size={4}>
							<b>{r.name || "-"}</b>
							{r.isVariant && <Tag color="purple" style={{ marginLeft: 4, fontSize: 10 }}>Variante</Tag>}
						</Space>
						<div className="vp-sub">{fmtSO(r.sellOutFrom)} → {fmtSO(r.sellOutTo)}</div>
					</div>
				</Space>
			)
		},
		{
			title: "Consorzio / Canale",
			render: (_, r) => (
				<div>
					<div>{r.consorzioName || "-"}</div>
					<div style={{ marginTop: 4, display: "flex", alignItems: "center" }}>
						<Tag color="geekblue" bordered={false} className="vp-tag" style={{ color: "#00f" }}>
							{r.cooperativaName || "-"}
						</Tag>
						<div className="vp-sub"> - {r.canaleName || "-"}</div>
					</div>
				</div>
			)
		},
		{
			title: "Refs",
			align: "center",
			width: 90,
			render: (_, r) => {
				const count = r.refsCount ?? r.referencesCount ?? (Array.isArray(r.refs) ? r.refs.length : undefined) ?? (Array.isArray(r.references) ? r.references.length : undefined) ?? 0;
				return <Tag color={count > 0 ? "blue" : "red"} className="vp-tag">{count}</Tag>;
			}
		},
		{
			title: "PDF",
			align: "center",
			width: 140,
			render: (_, r) => {
				const hasPdf = (r.pageWidth ?? 0) > 0;
				const isProcessing = r.status === "PROCESSING";
				if (hasPdf) {
					const pageSize = formatPdfPageSize(r.pdfPageSize);
					return (
						<div>
							<Tooltip title="Scarica PDF">
								<Tag color="green" className="vp-tag"
									onClick={(event) => handleDownloadPdf(event, r)}
									style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
									Caricato <DownloadOutlined style={{ fontSize: 12 }} />
								</Tag>
							</Tooltip>
							{pageSize && (
								<Tooltip title="Formato pagina del PDF caricato">
									<div className="vp-sub" style={{ marginTop: 4, whiteSpace: "nowrap" }}>{pageSize}</div>
								</Tooltip>
							)}
						</div>
					);
				}
				if (isProcessing) return <Tag color="blue" className="vp-tag">In elaborazione</Tag>;
				return <Tag color="red" className="vp-tag">Assente</Tag>;
			}
		},
		{
			title: "Stato",
			dataIndex: "status",
			render: (_, r) => {
				const m = STATUS_STYLES[r.status] || { text: r.status, tagColor: "default" };
				if (r.status === "PROCESSING" && r.processingProgress > 0) {
					return <Tag color="blue" className="vp-tag">{m.text} ({r.processingProgress}%)</Tag>;
				}
				return <Tag color={m.tagColor} className="vp-tag">{m.text}</Tag>;
			}
		},
		{
			title: "Cards",
			dataIndex: "cardsCount",
			align: "center",
			width: 90,
			render: v => <Tag color={v ? "geekblue" : "default"} className="vp-tag">{v ?? 0}</Tag>
		}
	];

	if (isTrash) {
		columns.push({
			title: "Eliminato", dataIndex: "deletedAt", width: 180,
			render: v => <span className="vp-sub">{v ? new Date(v).toLocaleString() : "-"}</span>
		});
	} else {
		columns.push({
			title: "Aggiornato", dataIndex: "updatedAt", width: 160,
			render: v => <span className="vp-sub">{v ? new Date(v).toLocaleString() : "-"}</span>
		});
	}

	columns.push({
		title: "Azioni", align: "center", width: "1%",
		render: (_, r) => (
			<FlyerActionsMenu
				row={r}
				onManage={onManage}
				onRefresh={onRefresh}
				variant={isTrash ? "trash" : "default"}
			/>
		)
	});

	const expandableConfig = (!flat && !isTrash) ? {
		expandedRowKeys,
		onExpandedRowsChange: setExpandedRowKeys,
		rowExpandable: (record) => (variantsByParentId.get(record._id || record.id) || []).length > 0,
		expandedRowRender: (record) => {
			const variants = variantsByParentId.get(record._id || record.id) || [];
			if (!variants.length) return null;
			return (
				<div className="vp-variants-wrap" onClick={e => e.stopPropagation()}>
					<div className="vp-variants-label">
						<BranchesOutlined style={{ marginRight: 6 }} />
						{variants.length === 1 ? "1 Variante" : `${variants.length} Varianti`}
					</div>
					<Table
						rowKey={r => r._id || r.id}
						columns={columns.map((col, idx) => idx === columns.length - 1 ? col : ({
							...col,
							onCell: (varRecord) => ({
								onClick: (e) => { e.stopPropagation(); handleRowClick(varRecord); }
							})
						}))}
						dataSource={variants}
						pagination={false}
						size="small"
						className="vp-sub-table"
					/>
				</div>
			);
		},
	} : undefined;

	const table = (
		<Table
			rowKey={r => r._id || r.id}
			columns={columns}
			dataSource={parentRows}
			loading={loading}
			expandable={expandableConfig}
			pagination={{ pageSize, showTotal: t => `${t} volantini${isTrash ? " eliminati" : ""}` }}
			onRow={(record) => ({
				onClick: (event) => {
					if (isTrash) return;
					const target = event.target;
					const actionsCell = target.closest("td:last-child");
					if (actionsCell) return;
					handleRowClick(record);
				}
			})}
		/>
	);

	if (embedded) return table;

	return (
		<Card className="wow-table-card card-elev">
			{table}
		</Card>
	);
}
